import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session lives in an httpOnly cookie set by the backend — nothing sensitive in localStorage.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [token, setToken] = useState(null); // in-memory only; cookie is the source of truth

  const applyToken = useCallback((t) => {
    if (t) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
    setToken(t);
  }, []);

  useEffect(() => {
    let active = true;
    // CRITICAL: skip /me during Google OAuth callback; AuthCallback will exchange the session_id.
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setUser(false);
      return;
    }
    async function check() {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (active) setUser(data);
      } catch {
        if (active) setUser(false);
      }
    }
    check();
    return () => { active = false; };
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    applyToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, accepted_terms = false) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, accepted_terms });
    applyToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const adoptSession = useCallback((token, userData) => {
    applyToken(token);
    setUser(userData);
  }, [applyToken]);

  const logout = () => {
    // best-effort backend logout to clear the httpOnly cookie + session row
    axios.post(`${API}/auth/logout`, {}).catch(() => {});
    applyToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, refresh, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
